import { useEffect, useState } from "react";
import { Body } from "@sprout/typography";
import { SecondaryButton, SecondaryButtonLink } from "@sprout/buttons";
import VerificationIcon from "@/sprout/icons/verification";
import useTranslation from "@hooks/use_translation";
import useTasks from "@hooks/use_tasks";
import useVerificationFlow from "@hooks/use_verification_flow";
import type { GhrTask, Task } from "@/types/task";
import { identityVerificationPartnerConfig } from "@/utils/identity_verification_provider";

const qualifies = (task: Task): task is GhrTask =>
  task.taskable_type === "IdentityVerificationTask" &&
  !task.dismissed &&
  (task as GhrTask).application_id == null;

const VerificationButton = ({ task }: { task: GhrTask }) => {
  const { t } = useTranslation("identity_verification");
  const { openModal, modal } = useVerificationFlow(task);

  return (
    <>
      {task.status === "processing_data" ? (
        <SecondaryButton size="medium" disabled>
          {t("status.processing_data.label")}
        </SecondaryButton>
      ) : task.status === "awaiting_user_input" && task.verification_url ? (
        <SecondaryButtonLink size="medium" href={task.verification_url} external>
          {t("cta.continue_verification")}
        </SecondaryButtonLink>
      ) : (
        <SecondaryButton size="medium" onClick={openModal}>
          {t("cta.verify_identity")}
        </SecondaryButton>
      )}
      {modal}
    </>
  );
};

const IdentityVerificationBanner = () => {
  const { data: tasks } = useTasks();
  const { t } = useTranslation("tasks");

  // Prevents banner from disappearing on inertia re-fetch when the application is synced
  // Theoretically, there can be multiple tasks, but we only show one banner
  const [latchedIds, setLatchedIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    setLatchedIds((prev) => {
      let next = prev;
      for (const task of tasks) {
        if (qualifies(task) && !prev.has(task.id)) {
          if (next === prev) {
            next = new Set(prev);
          }
          next.add(task.id);
        }
      }
      return next;
    });
  }, [tasks]);

  // Do use latest inertia data to have latest task status for button text
  const bannerTask = tasks.find((task) => latchedIds.has(task.id) && !task.dismissed) as
    | GhrTask
    | undefined;

  if (!bannerTask) {
    return null;
  }

  return (
    <div className="identity-verification-banner w-full bg-pale-blue rounded-none md:rounded-lg px-6 py-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center bg-gray-500 rounded w-[30px] h-[30px] shrink-0">
            <VerificationIcon size="sm" />
          </div>
          <div className="flex flex-col">
            <Body medium>
              {t("identityVerificationBanner.title", { company: bannerTask.company_name })}
            </Body>
            <Body>
              {t("identityVerificationBanner.body", {
                provider_name: identityVerificationPartnerConfig(bannerTask.partner).name,
              })}
            </Body>
          </div>
        </div>
        <div className="w-full md:w-auto md:shrink-0 [&_.btn]:w-full md:[&_.btn]:w-auto">
          <VerificationButton task={bannerTask} />
        </div>
      </div>
    </div>
  );
};

export default IdentityVerificationBanner;
